import { useId } from 'react';
import { cn } from '@/lib/utils';

/**
 * Kim scores every reply from 0 to 1, and two thresholds decide what happens to it:
 *
 *   below show-draft            hidden from agents      (ai_settings.confidenceThreshold, backend)
 *   show-draft up to auto-send  shown to an agent       (the agent decides)
 *   auto-send and above         Kim sends it on its own (automation_threshold, read by Brahma)
 */

const STEP = 0.05;

interface ThresholdSliderProps {
	label: string;
	hint: string;
	value: number;
	onChange: (value: number) => void;
	error?: string;
	disabled?: boolean;
}

/** A 0–1 score picked on a filled track or typed in the box beside it (Figma "Slosh Slider"). */
export function ThresholdSlider({ label, hint, value, onChange, error, disabled }: ThresholdSliderProps) {
	const id = useId();
	const filled = Number.isFinite(value) ? Math.min(1, Math.max(0, value)) : 0;
	return (
		<div className='flex w-full flex-col gap-1'>
			<div className='flex w-full flex-col gap-2'>
				<label htmlFor={id} className='text-[12px] font-medium leading-4 text-gray-900'>
					{label}
				</label>
				<div className='flex w-full items-start gap-2'>
					<div className='relative h-8 min-w-0 flex-1 overflow-hidden rounded-[20px] border border-gray-500 bg-gray-300'>
						<div className='absolute inset-y-0 left-0 bg-indigo-500' style={{ width: `${filled * 100}%` }} />
						<div className='absolute inset-y-1 w-[3px] -translate-x-full rounded-full bg-white' style={{ left: `${filled * 100}%` }} />
						<input
							type='range'
							min={0}
							max={1}
							step={STEP}
							value={filled}
							disabled={disabled}
							aria-label={label}
							onChange={(e) => onChange(Number(e.target.value))}
							className='absolute inset-0 size-full cursor-pointer opacity-0 disabled:cursor-not-allowed'
						/>
					</div>
					<input
						id={id}
						type='number'
						min={0}
						max={1}
						step={STEP}
						disabled={disabled}
						value={Number.isFinite(value) ? value : ''}
						// An empty box is NaN: it shows empty and the range error blocks Save until it is filled.
						onChange={(e) => onChange(e.target.valueAsNumber)}
						className={cn(
							'h-8 w-[67px] shrink-0 rounded-[12px] border bg-white px-3 text-center text-[13px] text-ink outline-none focus-visible:border-indigo-500 disabled:opacity-50',
							error ? 'border-red-500' : 'border-gray-500'
						)}
					/>
				</div>
			</div>
			{error ? <p className='text-[11px] leading-4 text-red-500'>{error}</p> : <p className='text-[11px] leading-4 text-gray-800'>{hint}</p>}
		</div>
	);
}
